import { forwardRef, useEffect, useState } from "react";
import {
  Button,
  TextField,
  Grid,
  LinearProgress,
  InputAdornment,
  Box,
  IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { SelectItem } from "../../../../components/Selectors/SelectItems";
import { DataGrid } from "@mui/x-data-grid";
import ResponsiveDataGrid from "../../../../components/MobileList/ResponsiveDataGrid";
import { BiEdit, BiTrash } from "react-icons/bi";
import ConfirmationDialog from "../../../../helpers/ConfirmationDialog";
import {
  PosDiscount,
  resetForm,
  setDialog,
} from "../../../../app/slices/posDiscountSlice";
import { HiOutlineReceiptPercent, HiOutlineTag } from "react-icons/hi2";
import FormDialog, {
  SectionCard,
  PlainCard,
} from "../../../../components/FormDialog";

const PosDiscountData = (props) => {
  const dispatch = useDispatch();

  const _Pos_id = useSelector((state) => state.posDiscount.pos_id);
  const _PosDiscount = useSelector((state) => state.posDiscount.posDiscount);
  const _PosDiscountData = useSelector(
    (state) => state.posDiscount.posDiscounts
  );
  const loading = useSelector((state) => state.posDiscount.posDiscount.loading);
  const items = useSelector((state) => state.items.itemsList.data);

  const isAdd = _PosDiscount.form.id ? false : true;
  const { control, handleSubmit, setValue, reset } = useForm({
    defaultValues: _PosDiscount.form,
  });

  const NumericFormatCustom = forwardRef(function NumericFormatCustom(
    props,
    ref
  ) {
    const { onChange, ...other } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator
        valueIsNumericString
      />
    );
  });

  const onSubmit = (data) => {
    if (isAdd) {
      dispatch(
        PosDiscount.create(
          {
            pos_id: _Pos_id,
            item_id: data.item_id,
            price: parseInt(data.price),
          },
          false
        )
      ).then(() => {
        dispatch(resetForm());
        reset({ ..._PosDiscount.form });
      });
    } else {
      dispatch(
        PosDiscount.update(
          data.id,
          {
            pos_id: _Pos_id,
            item_id: data.item_id,
            price: parseInt(data.price),
          },
          _Pos_id,
          false
        )
      );
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
  }, [dispatch]);

  useEffect(() => {
    reset({ ..._PosDiscount.form });
  }, [reset, _PosDiscount.form]);

  const handleDialog = () => {
    dispatch(setDialog());
  };

  // ---------------------- table logic --------------------------- //

  const [openConfirm, setConfirmOpen] = useState(false);
  const [dialogData, setDialogData] = useState({});

  const handleDeleteClick = (params) => {
    setConfirmOpen(true);
    setDialogData(params);
  };

  const handleConfirmDelete = () => {
    dispatch(PosDiscount.deleteById(dialogData.id, _Pos_id, false)).then(() => {
      dispatch(resetForm());
      reset({ ..._PosDiscount.form });
    });
    setConfirmOpen(false);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  const handleCellClick = (params) => {
    if (params.field === "operations") {
      return;
    } else {
      dispatch(PosDiscount.getById(params.row.id));
    }
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "item_name",
      headerName: "اسم المنتج",
      flex: 1,
    },
    {
      field: "price",
      headerName: "السعر بعد الخصم (IQD)",
      flex: 1,
      renderCell: (params) => parseInt(params.row.price)?.toLocaleString(),
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              color="primary"
              onClick={() => dispatch(PosDiscount.getById(params.row.id))}
            >
              <BiEdit />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <BiTrash />
            </IconButton>
          </>
        );
      },
    },
  ];

  return (
    <FormDialog
      open={true}
      onClose={() => {
        handleDialog();
      }}
      icon={<HiOutlineReceiptPercent />}
      title="الخصم"
      subtitle={
        isAdd
          ? "أدخل معلومات الخصم لإضافته"
          : "قم بتحديث معلومات الخصم ثم احفظ التغييرات"
      }
      onSubmit={handleSubmit(onSubmit)}
      saving={loading}
      extraActions={
        <Button
          onClick={() => {
            dispatch(resetForm());
            reset({ ..._PosDiscount.form });
          }}
          variant="contained"
          color="secondary"
        >
          تفريغ الحقول
        </Button>
      }
    >
      <ConfirmationDialog
        open={openConfirm}
        title="تاكيد"
        message="هل انت متاكد من حذف"
        name={dialogData.item_name}
        confirmText="حذف"
        cancelText="الغاء"
        onConfirm={() => handleConfirmDelete()}
        onCancel={handleCancelDelete}
      />
      <SectionCard icon={<HiOutlineTag />} title="معلومات الخصم">
        <Grid item xs={12} sm={6}>
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                required
                size="small"
                label="المبلغ"
                fullWidth
                {...field}
                InputProps={{
                  inputComponent: NumericFormatCustom,
                  startAdornment: (
                    <InputAdornment position="start">IQD</InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="item_id"
            control={control}
            render={({ field }) => (
              <SelectItem
                required
                onChange={(e, newValue) => {
                  setValue("item_id", newValue ? newValue.id : null, {
                    shouldDirty: true,
                  });
                }}
                value={items.filter((x) => x.id === field.value)[0] ?? null}
              />
            )}
          />
        </Grid>
      </SectionCard>
      <PlainCard>
        <Box sx={{ height: { xs: "auto", md: "60vh" }, width: "100%" }}>
          <ResponsiveDataGrid gridHeight={null}
            rows={
              _PosDiscountData.data?.map((x, index) => ({
                ...x,
                index: index + 1,
              })) ?? []
            }
            // onCellClick={(params) => {
            //   handleCellClick(params);
            // }}
            columns={columns}
            rowHeight={50}
            pagination
            selectionModel={[]}
            rowsPerPageOptions={[15, 25, 50, 100]}
            components={{
              LoadingOverlay: LinearProgress,
            }}
          />
        </Box>
      </PlainCard>
    </FormDialog>
  );
};

export default PosDiscountData;
